import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Input } from '../components/ui/input.js';
import { Card, CardBody } from '../components/ui/card.js';
import { Pill } from '../components/ui/pill.js';
import { QueryError } from '../components/ui/query-error.js';
import { ApiError, api, type CustomerSummary } from '../lib/api.js';

interface CustomersSearchParams {
  q?: string;
  customerNo?: string;
}

export function CustomersSearchScreen() {
  const navigate = useNavigate({ from: '/customers' });
  const search = useSearch({ from: '/customers' }) as CustomersSearchParams;
  const q = search.q ?? '';
  const customerNo = search.customerNo ?? '';

  const [qDraft, setQDraft] = useState(q);
  const [customerNoDraft, setCustomerNoDraft] = useState(customerNo);

  useEffect(() => {
    if (qDraft === q && customerNoDraft === customerNo) return;
    const id = setTimeout(() => {
      void navigate({
        search: {
          q: qDraft || undefined,
          customerNo: customerNoDraft || undefined,
        },
      });
    }, 250);
    return () => clearTimeout(id);
  }, [qDraft, customerNoDraft, q, customerNo, navigate]);

  const hasFilter = Boolean(q || customerNo);

  const query = useQuery({
    queryKey: ['customers', 'search', q, customerNo],
    queryFn: ({ signal }) =>
      api.customers.search(
        { q: q || undefined, customerNo: customerNo || undefined, limit: 50 },
        signal,
      ),
    enabled: hasFilter,
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
      <div className="grid grid-cols-[140px_1fr] gap-3">
        <Input
          aria-label="Customer number"
          placeholder="ACME"
          value={customerNoDraft}
          onChange={(e) => setCustomerNoDraft(e.target.value.trim().toUpperCase())}
          className="font-mono"
          autoCapitalize="characters"
          autoCorrect="off"
        />
        <Input
          aria-label="Search customer name"
          type="search"
          placeholder="Search customer name"
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          autoCorrect="off"
          autoComplete="off"
        />
      </div>

      {!hasFilter ? (
        <Card>
          <CardBody>
            <div className="text-sm text-ink-muted">
              Enter a customer number or name to search.
            </div>
          </CardBody>
        </Card>
      ) : (
        <Results query={query} q={q} customerNo={customerNo} />
      )}
    </div>
  );
}

interface ResultsProps {
  query: ReturnType<typeof useQuery<CustomerSummary[], ApiError>>;
  q: string;
  customerNo: string;
}

function Results({ query, q, customerNo }: ResultsProps) {
  if (query.isError) {
    return (
      <QueryError
        title="Couldn't load customers"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }
  if (query.isPending) return <SkeletonList />;

  const customers = query.data ?? [];
  if (customers.length === 0) {
    return (
      <Card>
        <CardBody>
          <div className="text-sm text-ink-muted">
            No customers match{' '}
            {customerNo ? (
              <strong>"{customerNo}"</strong>
            ) : (
              <strong>"{q}"</strong>
            )}.
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <ul className="space-y-2">
      {customers.map((c) => (
        <li key={c.spireId}>
          <CustomerCard customer={c} />
        </li>
      ))}
    </ul>
  );
}

function CustomerCard({ customer }: { customer: CustomerSummary }) {
  return (
    <Link
      to="/customers/$id"
      params={{ id: String(customer.spireId) }}
      className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 rounded-xl"
    >
      <Card className="transition-shadow hover:shadow-md active:shadow-sm">
        <CardBody className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-ink">{customer.customerNo}</span>
              {customer.hold && <Pill variant="danger">Hold</Pill>}
            </div>
            <div className="mt-0.5 text-sm text-ink truncate">{customer.name}</div>
            <div className="mt-0.5 text-xs text-ink-subtle truncate">
              {[customer.address.city, customer.address.provState].filter(Boolean).join(', ') ||
                '—'}
            </div>
          </div>
          <div className="text-right shrink-0 text-xs text-ink-subtle">
            <div>open ${customer.openOrdersTotal}</div>
            <div className="mt-0.5">L{customer.sellLevel}</div>
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2" aria-busy="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <Card>
            <CardBody className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <div className="h-4 w-24 bg-surface-muted rounded animate-pulse" />
                <div className="h-3 w-3/4 bg-surface-muted rounded animate-pulse" />
              </div>
              <div className="h-4 w-16 bg-surface-muted rounded animate-pulse" />
            </CardBody>
          </Card>
        </li>
      ))}
    </ul>
  );
}
